import { Check, ChevronRight, Upload } from "lucide-react";
import { useRef, useState } from "react";

const MAX_SIZE_MB = 10;

const UploadField = ({
  title,
  description,
  file,
  accept = "image/*,application/pdf",
  capture,
  error,
  onChange,
}: {
  title: string;
  description: string;
  file?: File;
  accept?: string;
  capture?: "user" | "environment";
  error?: string;
  onChange: (file?: File) => void;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string>();
  const shownError = fileError ?? error;

  const handleFile = (picked?: File) => {
    if (!picked) return;
    const allowed =
      picked.type.startsWith("image/") ||
      (accept.includes("application/pdf") && picked.type === "application/pdf");
    if (!allowed) {
      setFileError(
        accept.includes("pdf")
          ? "Upload an image or a PDF"
          : "Upload an image file",
      );
      return;
    }
    if (picked.size > MAX_SIZE_MB * 1024 * 1024) {
      setFileError(`File must be ${MAX_SIZE_MB}MB or smaller`);
      return;
    }
    setFileError(undefined);
    onChange(picked);
  };
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className={`bg-auth-track flex w-full items-center gap-3 rounded-lg border border-dashed p-3 text-left transition-colors hover:bg-black/[0.02] ${shownError ? "border-red-500" : "border-auth-stroke"}`}
      >
        <span className="bg-auth-stroke/40 flex size-10 shrink-0 items-center justify-center rounded">
          {file ? (
            <Check className="text-auth-accent size-5" />
          ) : (
            <Upload className="text-auth-muted size-5" />
          )}
        </span>
        <span className="min-w-0 grow">
          <span className="text-auth-muted block text-base font-medium">
            {title}
          </span>
          <span className="text-auth-muted block truncate text-[10px]">
            {file ? file.name : description}
          </span>
        </span>
        <ChevronRight className="text-auth-muted size-5 shrink-0" />
      </button>
      <input
        ref={ref}
        type="file"
        hidden
        accept={accept}
        capture={capture}
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      {shownError && <p className="text-destructive text-sm">{shownError}</p>}
    </div>
  );
};

export default UploadField;
